import { CircleCheck, Link as LinkIcon } from 'lucide-react';
import { Link } from 'react-router';
import { trpc } from '../../../lib/trpc';
import { Mono } from '../../../ui/Mono';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { ClientDot } from './ClientDot';
import { ConflictActions } from './ConflictActions';

/**
 * Lista konfliktów emaili kontaktów (ustawienia klienta albo zbiorczo): ten sam adres u kilku klientów z akcjami
 * „To ta sama osoba — zostaw u X” / „To różne osoby — zostaw”. Pokazuje tylko kontakty widoczne dla osoby.
 */
export function ContactConflictList({ clientId }: { clientId?: string }) {
  const conflicts = trpc.clients.contacts.conflicts.useQuery({ clientId });

  if (conflicts.isPending) return <ListSkeleton rows={2} />;
  if (conflicts.isError) {
    return <ErrorState compact onRetry={() => void conflicts.refetch()} retrying={conflicts.isFetching} />;
  }
  if (conflicts.data.length === 0) {
    return (
      <EmptyState
        compact
        icon={CircleCheck}
        title="Brak konfliktów kontaktów"
        description="Żaden email kontaktu nie powtarza się u innego klienta."
      />
    );
  }

  return (
    <ul className="border-t border-line">
      {conflicts.data.map((group) => (
        <li key={group.email} className="flex flex-col gap-3 border-b border-line py-4">
          <div className="flex items-center gap-2">
            <LinkIcon size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-warning" />
            <Mono className="text-body-s text-fg">{group.email}</Mono>
            <span className="text-caption text-fg-muted">
              u {new Set(group.contacts.map((c) => c.clientId)).size} klientów
            </span>
          </div>
          <ul className="flex flex-col gap-1.5 pl-6">
            {group.contacts.map((c) => (
              <li key={c.contactId} className="flex items-center gap-2 text-body-s">
                <ClientDot color={c.clientColor} />
                <Link
                  to={`/klienci/${c.clientId}`}
                  className="rounded-xs text-fg hover:underline focus-visible:outline-2 focus-visible:outline-accent"
                >
                  {c.clientName}
                </Link>
                <span className="text-fg-muted">
                  · {c.name}
                  {c.role ? `, ${c.role}` : ''}
                  {c.isPrimary ? ' · główny' : ''}
                </span>
              </li>
            ))}
          </ul>
          <div className="pl-6">
            {group.canResolve ? (
              <ConflictActions
                email={group.email}
                variant="buttons"
                members={group.contacts.map((c) => ({
                  contactId: c.contactId,
                  name: c.name,
                  clientId: c.clientId,
                  clientName: c.clientName,
                  clientColor: c.clientColor,
                }))}
              />
            ) : (
              <p className="text-caption text-fg-muted">
                Rozstrzygnąć może osoba z edycją wszystkich klientów z tym adresem.
              </p>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
